import * as THREE from 'three';
import {OperationGroup, Dimension} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {AnimationHandler} from 'components/customer/Preview3D/animation/entity/AnimationHandler';
import {toggleDoorHandler} from 'components/customer/Preview3D/animation/doorAnimationHandler';
import {toggleCornerHandler} from 'components/customer/Preview3D/animation/cornerAnimationHandler';
import {toggleDrawerHandler} from 'components/customer/Preview3D/animation/drawerAnimationHandler';
import {toggleApplianceExtendedDoorHandler} from 'components/customer/Preview3D/animation/applianceAnimationHandler';
import {toggleMainBifoldDoorHandler} from 'components/customer/Preview3D/animation/mainBifoldDoorAnimationHandler';
import {toggleExtendedBifoldDoorHandler} from 'components/customer/Preview3D/animation/extendedBifoldDoorAnimationHandler';
import {RoomPlannerFormValues} from 'components/customer/RoomPlanner/types';
import {highSwingAnimationHandler} from 'components/customer/Preview3D/animation/highSwingAnimationHandler';
import {highLiftAnimationHandler} from 'components/customer/Preview3D/animation/highLiftAnimationHandler';
import {toggleCornerDrawerHandler} from 'components/customer/Preview3D/animation/cornerDrawerAnimationHandler';
import {getPartAttributes} from 'components/customer/RoomPlanner/helpers/getProductAttributes';
import {
    getFoldKitReturnPanelWidth,
    toggleFoldKitDoorHandler,
} from 'components/customer/Preview3D/animation/foldKitDoorAnimationHandler';
import {createReturnDoorAnimationHandler} from 'components/customer/Preview3DCommon/animations/returnDoor';
import {getPantryAwareRightLShapeBifoldDoorX} from 'components/customer/Preview3DCommon/values/door';
import {isSplitDoorOperationGroup} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';

const getMeshSize = (
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh
) => {
    const bbox = new THREE.Box3().setFromObject(mesh);
    const size = new THREE.Vector3();
    bbox.getSize(size);
    return size;
};

export const attachAnimations = (
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh,
    operationGroup: OperationGroup,
    values: RoomPlannerFormValues,
    variables: TemplateVariable,
    meshPos: Dimension,
    counter: number
) => {
    const animation = animationHandler(
        mesh,
        operationGroup,
        values,
        variables,
        meshPos,
        counter
    );
    /** Used by Room Planner bounds: `toggleDoor` exists on all panels, but only door/drawer ops get an animation. */
    mesh.userData.boundsExcludeAnimatedDoorOrDrawerPart = Boolean(animation);
    mesh.userData.toggleDoor = async ({
        isDoorOrDrawerOpen,
        animationSpeed,
    }: {
        isDoorOrDrawerOpen: boolean;
        animationSpeed?: number;
    }) => {
        if (!animation) return;
        const {open, close} = animation;

        if (isDoorOrDrawerOpen) {
            if (!open.length) return;
            await Promise.all(
                open.map((openAnimation) => openAnimation(animationSpeed, true))
            );
            return;
        }

        if (!close.length) return;
        await Promise.all(
            close.map((closeAnimation) => closeAnimation(animationSpeed))
        );
    };
};

export const animationHandler = (
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh,
    operationGroup: OperationGroup,
    values: RoomPlannerFormValues,
    variables: TemplateVariable,
    meshPos: Dimension,
    counter: number
): AnimationHandler => {
    const exteriorThickness = values.cabinet_ext_thickness;
    const carcaseThickness = values.cabinet_carc_thickness;

    const {isDrawer, isInnerDrawer, isInnerDrawerOnly} = getPartAttributes(
        operationGroup,
        variables,
        values
    );
    let widthMultiplier = 2;
    let returnWidthOffset = 0;

    const handleReturnDoor = ({
        mesh,
        variables,
        values,
        isRight,
        isPairDoor,
    }: {
        mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh;
        variables: TemplateVariable;
        values: RoomPlannerFormValues;
        isRight: boolean;
        isPairDoor: boolean;
    }) =>
        createReturnDoorAnimationHandler({
            mesh,
            variables,
            values,
            isRight,
            isPairDoor,
            carcaseThickness: Number(values?.cabinet_carc_thickness ?? 0),
            hingeFixup: 'hinge',
            getMeshSize,
        });

    if (variables?.isReturnProduct && variables?.isLeftReturn) {
        returnWidthOffset =
            Number(values.cabinet_return_panel_width) -
            values.cabinet_void_width;
    }

    if (
        variables?.isApplianceLeftBifoldRight ||
        variables?.isApplianceLeftRightBifold
    ) {
        widthMultiplier = 3;
    }

    if (variables?.isApplianceBifoldPair) {
        widthMultiplier = 4;
    }

    const keepsShakerPlacement =
        mesh.name === 'ShakerDoors' &&
        !isSplitDoorOperationGroup(operationGroup);

    if (operationGroup.IsRightDoor) {
        let positionXOffset = returnWidthOffset;
        if (variables.isCabinetUsingExterior) {
            positionXOffset += exteriorThickness;
        }
        const returnDoorHandler = handleReturnDoor({
            mesh,
            variables,
            values,
            isRight: true,
            isPairDoor: !!operationGroup.IsPairDoor,
        });
        if (returnDoorHandler) {
            return returnDoorHandler;
        }
        if (operationGroup.IsPairDoor && !keepsShakerPlacement) {
            const size = getMeshSize(mesh);
            const doorGap = Number(values.cabinet_door_gap) || 0;
            const doorRightMargin = Number(values.cabinet_door_right) || 0;
            if (!variables.is3DoorProduct) {
                mesh.position.x =
                    size.x * widthMultiplier +
                    exteriorThickness * 0.2 +
                    doorGap +
                    doorRightMargin +
                    positionXOffset;
            } else {
                mesh.position.x += size.x + positionXOffset;
            }
            mesh.scale.x = -1;
        }
        if (variables.hasLShapedShelves) {
            operationGroup.AnimationDelay = '375';
        }
        return toggleDoorHandler({
            mesh,
            variables,
            isRightDoor: true,
            isThreeDoor: variables.is3DoorProduct,
            degrees: 90,
            animationDelay: operationGroup.AnimationDelay
                ? Number(operationGroup.AnimationDelay)
                : undefined,
        });
    }

    if (operationGroup.IsLeftDoor) {
        let positionXOffset = returnWidthOffset;
        if (variables.isCabinetUsingExterior) {
            positionXOffset += exteriorThickness;
        }
        const returnDoorHandler = handleReturnDoor({
            mesh,
            variables,
            values,
            isRight: false,
            isPairDoor: !!operationGroup.IsPairDoor,
        });
        if (returnDoorHandler) {
            return returnDoorHandler;
        }
        if (!keepsShakerPlacement) {
            const doorLeftMargin = Number(values.cabinet_door_left) || 0;
            mesh.position.x = doorLeftMargin + positionXOffset;
            mesh.scale.x = -1;
        }
        return toggleDoorHandler({
            mesh,
            variables,
            degrees: 90,
            animationDelay: operationGroup.AnimationDelay
                ? Number(operationGroup.AnimationDelay)
                : undefined,
        });
    }

    if (operationGroup.IsTopHangDoor) {
        const typedMesh = mesh as THREE.Group<THREE.Object3DEventMap>;

        if (variables.isHighSwing) {
            const size = getMeshSize(typedMesh);
            const doorBottomMargin = Number(values?.cabinet_door_bottom) || 0;
            typedMesh.position.z = size.z + doorBottomMargin;
            typedMesh.scale.y = -1;
            return highSwingAnimationHandler(typedMesh, 'z', undefined);
        }

        if (variables.isHighLift) {
            const size = getMeshSize(typedMesh);
            const endLiftDistance = size.z + size.z * 0.01;

            return highLiftAnimationHandler(
                typedMesh,
                endLiftDistance,
                undefined
            );
        }

        const size = getMeshSize(mesh);
        const doorLeftMargin = Number(values.cabinet_door_left) || 0;
        mesh.position.x = doorLeftMargin;
        mesh.position.z = size.z;
        mesh.scale.x = -1;
        mesh.scale.y = -1;
        return toggleDoorHandler({
            mesh,
            variables,
            isTopHangDoor: true,
            degrees: 90,
        });
    }

    if (operationGroup.IsLeftApplianceMainDoor) {
        mesh.scale.x = -1;

        const boundingBox = new THREE.Box3().setFromObject(mesh);
        const width = boundingBox.max.x - boundingBox.min.x;
        mesh.position.x -= width;

        return toggleDoorHandler({
            mesh,
            variables,
            degrees: 87,
        });
    }

    if (operationGroup.IsRightApplianceMainDoor) {
        const size = getMeshSize(mesh);
        mesh.position.x = size.x * widthMultiplier + exteriorThickness * 0.2;
        mesh.scale.x = -1;
        return toggleDoorHandler({
            mesh,
            variables,
            isRightDoor: true,
            degrees: 87,
        });
    }

    if (operationGroup.IsCornerLeftDoor || operationGroup.IsCornerRightDoor) {
        const isRightDoor = Boolean(operationGroup.IsCornerRightDoor);
        if (isRightDoor) {
            mesh.position.y =
                values.cabinet_left_width - values.cabinet_right_depth;
            mesh.position.x +=
                values.cabinet_right_width - values.cabinet_left_depth;
            mesh.scale.x = -1;
        }
        return toggleCornerHandler({
            mesh: mesh as THREE.Group<THREE.Object3DEventMap>,
            isRightDoor,
        });
    }

    if (isDrawer && !isInnerDrawerOnly) {
        const animation = toggleDrawerHandler(
            mesh as THREE.Group<THREE.Object3DEventMap>,
            operationGroup,
            variables,
            values,
            counter
        );
        if (isInnerDrawer && animation?.close?.length) {
            void animation.close[0](0);
        }
        return animation;
    }

    if (operationGroup.IsRightLShape) {
        if (operationGroup.IsBifoldRightDoor) {
            mesh.scale.x = -1;
            mesh.position.x = getPantryAwareRightLShapeBifoldDoorX(
                mesh,
                meshPos.x,
                Boolean(variables?.isLShapedPantry),
                values.cabinet_right_width,
                Number(values.cabinet_door_right),
                exteriorThickness,
                carcaseThickness
            );
            return toggleMainBifoldDoorHandler({
                mesh: mesh as THREE.Group<THREE.Object3DEventMap>,
                isRightLShape: true,
            });
        }

        if (operationGroup.IsBifoldLeftDoor) {
            const size = getMeshSize(mesh);
            return toggleExtendedBifoldDoorHandler(
                mesh as THREE.Group<THREE.Object3DEventMap>,
                meshPos,
                variables,
                values,
                exteriorThickness,
                carcaseThickness,
                true,
                Boolean(operationGroup.IsLeftBifoldExtension),
                size.y,
                0
            );
        }
    } else {
        if (operationGroup.IsBifoldLeftDoor) {
            const size = getMeshSize(mesh);
            const door =
                Number(values.cabinet_ext_thickness) ||
                values.cabinet_carc_thickness ||
                16.5;
            return toggleMainBifoldDoorHandler({
                mesh: mesh as THREE.Group<THREE.Object3DEventMap>,
                pivotOffset: size.x - door,
            });
        }

        if (operationGroup.IsBifoldRightDoor) {
            return toggleExtendedBifoldDoorHandler(
                mesh as THREE.Group<THREE.Object3DEventMap>,
                meshPos,
                variables,
                values,
                exteriorThickness,
                carcaseThickness,
                false,
                false,
                0,
                0,
                operationGroup.AppliedDoorShift?.insertDepth ?? 0,
                operationGroup.AppliedDoorShift?.alongLeg ?? 0
            );
        }
    }

    if (operationGroup.IsLeftApplianceExtendedDoor) {
        const size = getMeshSize(mesh);
        mesh.position.x = size.x * 2 + exteriorThickness * 0.2;
        mesh.scale.x = -1;
        return toggleApplianceExtendedDoorHandler({
            mesh: mesh as THREE.Group<THREE.Object3DEventMap>,
            variables,
            values,
            carcaseThickness,
        });
    }

    if (operationGroup.IsRightApplianceExtendedDoor) {
        const size = getMeshSize(mesh);
        const meshPositionX = size.x * 2 + exteriorThickness * 0.2;
        if (variables?.isApplianceBifoldPair) {
            mesh.position.x = meshPositionX;
        } else if (variables?.isApplianceLeftRightBifold) {
            mesh.position.x = size.x;
        } else {
            mesh.position.x = 0;
        }
        mesh.scale.x = -1;
        return toggleApplianceExtendedDoorHandler({
            mesh: mesh as THREE.Group<THREE.Object3DEventMap>,
            variables,
            values,
            carcaseThickness,
            isRightExtendedDoor: true,
        });
    }

    if (operationGroup.IsCornerLeft || operationGroup.IsCornerRight) {
        return toggleCornerDrawerHandler({
            mesh,
            insert: meshPos,
            index: Number(operationGroup.Index),
        });
    }

    if (operationGroup.IsCorFoldKitRightDoor) {
        return toggleFoldKitDoorHandler({
            mesh,
            isRightDoor: true,
            values,
            carcaseThickness,
        });
    }

    if (operationGroup.IsCorFoldKitLeftDoor) {
        mesh.position.x = getFoldKitReturnPanelWidth(values);
        return toggleFoldKitDoorHandler({
            mesh,
            values,
            carcaseThickness,
        });
    }

    if (mesh.userData.isCornerDrawerPart) {
        return toggleCornerDrawerHandler({
            mesh,
            insert: meshPos,
            index: counter,
        });
    }
};
